/**
 * Timeout Handling
 * 
 * Provides utilities for managing request timeouts using AbortController.
 * 
 * Features:
 * - Automatic request cancellation after timeout
 * - Proper cleanup of timeout timers
 * - Type-safe timeout error detection
 * - Graceful error transformation
 * 
 * @module timeout
 */

import { API_CONFIG, ERROR_CODES, IS_PRODUCTION } from '../constants';
import { ApiError } from '../errors';

/**
 * Result of creating a timeout controller
 */
export interface TimeoutController {
  /**
   * AbortController instance
   * Pass controller.signal to fetch()
   */
  controller: AbortController;
  
  /**
   * Cleanup function
   * MUST be called to clear the timeout timer
   */
  cleanup: () => void;
}

/**
 * Create AbortController with timeout
 * 
 * **IMPORTANT:** Always call cleanup() when done to prevent memory leaks.
 * 
 * @param timeoutMs - Timeout in milliseconds (default: API_CONFIG.DEFAULT_TIMEOUT_MS)
 * @returns Object with controller and cleanup function
 * 
 * @example
 * ```typescript
 * const { controller, cleanup } = createTimeoutController(5000);
 * 
 * try {
 *   const response = await fetch('/api/data', {
 *     signal: controller.signal,
 *   });
 *   return response;
 * } finally {
 *   cleanup(); // Always cleanup!
 * }
 * ```
 */
export function createTimeoutController(
  timeoutMs: number = API_CONFIG.DEFAULT_TIMEOUT_MS
): TimeoutController {
  const controller = new AbortController();
  
  // Set timeout to abort request
  const timeoutId = setTimeout(() => {
    if (!IS_PRODUCTION) {
      console.warn(
        `[ApiClient] ⏱️ Request timed out after ${timeoutMs}ms`,
        '\nAborting request...'
      );
    }
    controller.abort();
  }, timeoutMs);
  
  // Cleanup function clears timeout
  const cleanup = () => {
    clearTimeout(timeoutId);
  };
  
  return { controller, cleanup };
}

/**
 * Check if error is a timeout error
 * 
 * Detects both:
 * - Native AbortError from AbortController
 * - Custom ApiError with TIMEOUT_ERROR code
 * 
 * @param error - Error to check
 * @returns true if error was caused by timeout
 * 
 * @example
 * ```typescript
 * try {
 *   await fetchWithTimeout('/api/data', 5000);
 * } catch (error) {
 *   if (isTimeoutError(error)) {
 *     console.log('Request timed out');
 *   }
 * }
 * ```
 */
export function isTimeoutError(error: unknown): boolean {
  // Native AbortError from AbortController
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  
  // Custom ApiError with TIMEOUT_ERROR code
  if (error instanceof ApiError && error.code === ERROR_CODES.TIMEOUT_ERROR) {
    return true;
  }
  
  return false;
}

/**
 * Wrap error as timeout error if it was caused by abort
 * 
 * Converts native DOMException abort errors to ApiError with
 * proper error code and user-friendly message.
 * 
 * @param error - Error to wrap
 * @returns ApiError with TIMEOUT_ERROR code
 * @throws Original error if not a timeout error
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await fetch(url, { signal });
 * } catch (error) {
 *   throw wrapTimeoutError(error);
 * }
 * ```
 */
export function wrapTimeoutError(error: unknown): never {
  if (isTimeoutError(error)) {
    throw new ApiError(
      'Request timed out',
      ERROR_CODES.TIMEOUT_ERROR,
      {
        userMessage: 'The request took too long. Please try again.',
        details: error instanceof Error ? error.message : undefined,
      }
    );
  }
  
  // Not a timeout error, throw original
  throw error;
}

/**
 * Execute fetch request with timeout
 * 
 * Convenience wrapper that handles timeout controller creation and cleanup.
 * 
 * @param input - Request URL or Request object
 * @param init - Request options
 * @param timeoutMs - Timeout in milliseconds
 * @returns Response from fetch
 * @throws ApiError with TIMEOUT_ERROR code if request times out
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await fetchWithTimeout('/api/data', {
 *     method: 'POST',
 *     body: JSON.stringify({ foo: 'bar' }),
 *   }, 5000);
 *   return response.json();
 * } catch (error) {
 *   if (isTimeoutError(error)) {
 *     console.log('Request timed out');
 *   }
 * }
 * ```
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs: number = API_CONFIG.DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const { controller, cleanup } = createTimeoutController(timeoutMs);
  
  try {
    const response = await fetch(input, {
      ...init,
      signal: controller.signal,
    });
    
    return response;
  } catch (error) {
    // Convert timeout errors to ApiError
    wrapTimeoutError(error);
  } finally {
    // Always cleanup timeout
    cleanup();
  }
}
